import {
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerBody,
  DrawerFooter,
  DrawerCloseButton,
  Button,
  Text,
  Flex,
  useToast,
  Box,
  Center,
} from "@chakra-ui/react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
  Elements,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { API, graphqlOperation } from "aws-amplify";
import { updateBookingSecurityItem } from "../../../../graphql/mutations";

import React, { useEffect, useState } from "react";
import CardConsentNote from "../../../Mini/cardConsentNote";

// Local dev can point payment calls at a local engine via REACT_APP_PAY_API_URL;
// production builds don't set it and fall back to the live API.
const PAY_API = process.env.REACT_APP_PAY_API_URL || "https://api.linkbase.ca";

const PerStayCardDrawerContent = ({
  isOpen,
  onClose,
  stripeAccountId,
  amount,
  currency,
  description,
  guest,
  booking,
  fullSecurityItem,
  totalPrice,
  onPaymentSuccess,
}) => {
  const toast = useToast();
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);

  // Track completeness of each card field
  const [cardComplete, setCardComplete] = useState(false);
  const [expiryComplete, setExpiryComplete] = useState(false);
  const [cvcComplete, setCvcComplete] = useState(false);

  const updateBookingSecurityItemStatus = async (paymentIntentId) => {
    try {
      // Find the DEPOSIT security item in the booking
      const depositSecurityItem = booking.securityItems.items.find(
        (item) => item.type === "DEPOSIT"
      );

      if (depositSecurityItem) {
        const bookingSecurityItemUpdateInput = {
          id: depositSecurityItem.id,
          status: "captured",
          isComplete: true,
          payIntentId: paymentIntentId,
        };

        await API.graphql(
          graphqlOperation(updateBookingSecurityItem, {
            input: bookingSecurityItemUpdateInput,
          })
        );

      } else {
        console.warn("No DEPOSIT security item found for this booking.");
      }
    } catch (error) {
      console.error("Error updating booking security item:", error);
    }
  };

  const handlePayment = async () => {
    if (!stripe || !elements) return;

    setLoading(true);

    const cardElement = elements.getElement(CardNumberElement);

    // Prevent payment if any field is incomplete
    if (!cardComplete || !expiryComplete || !cvcComplete) {
      toast({
        title: "Incomplete Card Info",
        description: "Please fill out all card fields.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    if (!cardElement) {
      toast({
        title: "Input Information",
        description: "Please input card information.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
      setLoading(false);
      return;
    }

    try {
      // Create PaymentMethod on connected account
      const { paymentMethod, error } = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: guest.name,
          email: guest.email,
        },
      });

      if (error) {
        toast({
          title: "Card Error",
          description: error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setLoading(false);
        return;
      }

      // Call the external API with the PaymentMethod
      const { Auth } = await import("aws-amplify");
      const session = await Auth.currentSession();
      const idToken = session.getIdToken().getJwtToken();

      const response = await axios.post(`${PAY_API}/payments/simple/process`, {
        amount: amount,
        currency: currency,
        connectedAccountId: stripeAccountId,
        paymentMethodId: paymentMethod.id,
        description: description,
      }, {
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json',
          'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
        },
      });

      const responseData = response.data;
      
      if (response.status === 200 && responseData.success) {
        // Check if 3D Secure action is required for automatic confirmation
        if (responseData.requiresAction && responseData.clientSecret) {
          
          try {
            // Handle 3D Secure authentication
            const confirmResult = await stripe.confirmCardPayment(responseData.clientSecret, {
              payment_method: {
                card: cardElement,
                billing_details: {
                  name: guest.name,
                  email: guest.email,
                },
              },
            });

            if (confirmResult.error) {
              
              // Handle specific error types
              let errorMessage = confirmResult.error.message;
              let errorTitle = "Payment Failed";
              
              if (confirmResult.error.code === 'card_declined') {
                if (confirmResult.error.decline_code === 'transaction_not_allowed') {
                  errorTitle = "Test Card Issue";
                  errorMessage = "This appears to be a test card that doesn't support this transaction type. Please use a different card or contact support.";
                } else {
                  errorTitle = "Card Declined";
                  errorMessage = `Your card was declined: ${confirmResult.error.decline_code || 'Unknown reason'}`;
                }
              } else if (confirmResult.error.code === 'authentication_required') {
                errorTitle = "Authentication Required";
                errorMessage = "Your card requires additional authentication. Please try again.";
              }
              
              toast({
                title: errorTitle,
                description: errorMessage,
                status: "error",
                duration: 6000,
                isClosable: true,
              });
              return;
            }

            if (confirmResult.paymentIntent.status === 'succeeded') {
              // Capture the payment
              try {
                const captureResponse = await axios.post(`${PAY_API}/payments/simple/capture`, {
                  paymentIntentId: responseData.paymentIntentId,
                  connectedAccountId: stripeAccountId,
                }, {
                  headers: {
                    'Authorization': `Bearer ${idToken}`,
                    'Content-Type': 'application/json',
                    'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
                  },
                });

                if (captureResponse.status === 200 && captureResponse.data.success) {
                  // Update booking security item status immediately
                  await updateBookingSecurityItemStatus(responseData.paymentIntentId);
                  
                  toast({
                    title: "Payment Successful",
                    description: "Per stay deposit has been processed and captured.",
                    status: "success",
                    duration: 4000,
                    isClosable: true,
                  });
                  onClose();
                } else {
                  toast({
                    title: "Capture Failed",
                    description: "Payment was authorized but capture failed.",
                    status: "error",
                    duration: 4000,
                    isClosable: true,
                  });
                }
              } catch (captureError) {
                console.error("Capture error:", captureError);
                toast({
                  title: "Capture Error",
                  description: "Payment was authorized but capture failed.",
                  status: "error",
                  duration: 4000,
                  isClosable: true,
                });
              }
            } else {
              toast({
                title: "Payment Incomplete",
                description: `Payment status: ${confirmResult.paymentIntent.status}`,
                status: "warning",
                duration: 4000,
                isClosable: true,
              });
            }
          } catch (confirmError) {
            console.error("3D Secure confirmation error:", confirmError);
            console.error("Error details:", {
              message: confirmError.message,
              stack: confirmError.stack,
              name: confirmError.name
            });
            toast({
              title: "3D Secure Error",
              description: "Failed to complete 3D Secure authentication.",
              status: "error",
              duration: 4000,
              isClosable: true,
            });
          }
        } else {
          // No 3D Secure required, payment is complete
          
          // Capture the payment
          try {
            const captureResponse = await axios.post(`${PAY_API}/payments/simple/capture`, {
              paymentIntentId: responseData.paymentIntentId,
              connectedAccountId: stripeAccountId,
            }, {
              headers: {
                'Authorization': `Bearer ${idToken}`,
                'Content-Type': 'application/json',
                'x-api-key': process.env.REACT_APP_LINKBASE_API_KEY,
              },
            });

            if (captureResponse.status === 200 && captureResponse.data.success) {
              // Update booking security item status immediately
              await updateBookingSecurityItemStatus(responseData.paymentIntentId);
              
              // Update parent state immediately
              if (onPaymentSuccess) {
                onPaymentSuccess();
              }
              
              toast({
                title: "Payment Successful",
                description: "Per stay deposit has been processed and captured.",
                status: "success",
                duration: 4000,
                isClosable: true,
              });
              onClose();
            } else {
              toast({
                title: "Capture Failed",
                description: "Payment was authorized but capture failed.",
                status: "error",
                duration: 4000,
                isClosable: true,
              });
            }
          } catch (captureError) {
            console.error("Capture error:", captureError);
            toast({
              title: "Capture Error",
              description: "Payment was authorized but capture failed.",
              status: "error",
              duration: 4000,
              isClosable: true,
            });
          }
        }
      } else {
        toast({
          title: "Payment Failed",
          description: responseData.message || "Payment processing failed.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("Per stay payment error:", err);
      toast({
        title: "Error",
        description: "Something went wrong during payment.",
        status: "error",
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Drawer isOpen={isOpen} placement="bottom" onClose={onClose}>
      <DrawerOverlay />
      <DrawerContent
        minH="35vh"
        borderTopRadius="2xl"
        maxW={{ base: "100%", md: "520px" }}
        mx="auto"
        mb={{ md: 8 }}
        borderRadius={{ md: "24px" }}
      >
        <Flex mx={3} my={4} align="center" justify="space-between">
          <Text fontSize="18px" fontWeight={800}>
            Pay Per Stay Deposit
          </Text>
          <DrawerCloseButton pos="relative" top={0} right={0} />
        </Flex>

        <DrawerBody p={3}>
          <Text fontWeight={800} fontSize="12px" pb="5px">
            Card Number
          </Text>
          <Box
            px={3}
            py={3}
            borderRadius="md"
            bg="#3e4757"
            mb={4}
            w="full"
          >
            <CardNumberElement
              options={{
                style: {
                  base: {
                    fontSize: "16px",
                    color: "#fff",
                    "::placeholder": { color: "#ccc" },
                    iconColor: "#ccc",
                    fontFamily: "inherit",
                  },
                },
              }}
              onChange={e => setCardComplete(e.complete)}
            />
          </Box>

          <Flex gap={4}>
            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                Expiry
              </Text>
              <Box
                px={3}
                py={3}
                borderRadius="md"
                bg="#3e4757"
                w="full"
              >
                <CardExpiryElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setExpiryComplete(e.complete)}
                />
              </Box>
            </Flex>

            <Flex flex={1} flexDirection="column">
              <Text fontWeight={800} fontSize="12px" pb="5px">
                CVC
              </Text>
              <Box
                px={3}
                py={3}
                borderRadius="md"
                bg="#3e4757"
                w="full"
              >
                <CardCvcElement
                  options={{
                    style: {
                      base: {
                        fontSize: "16px",
                        color: "#fff",
                        "::placeholder": { color: "#ccc" },
                        fontFamily: "inherit",
                      },
                    },
                  }}
                  onChange={e => setCvcComplete(e.complete)}
                />
              </Box>
            </Flex>
          </Flex>
        </DrawerBody>

        <DrawerFooter p={3} flexDirection="column" gap={2}>
          <Button
            onClick={handlePayment}
            isLoading={loading}
            bg="#817efc"
            color="white"
            h="42px"
            w="full"
            variant="solid"
            borderRadius="10px"
            fontSize="14px"
            disabled={!cardComplete || !expiryComplete || !cvcComplete}
            _disabled={{
              opacity: 0.5,
              cursor: "not-allowed",
              bg: "#817efc",
              color: "white",
              pointerEvents: "none"
            }}
          >
            Pay Now
          </Button>
          <CardConsentNote />
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};

export const PerStayCardDrawer = ({
  isOpen,
  onClose,
  stripeAccountId,
  amount,
  currency,
  description,
  guest,
  booking,
  fullSecurityItem,
  totalPrice,
  onPaymentSuccess,
}) => {
  const [stripePromise, setStripePromise] = useState(null);

  useEffect(() => {
    if (isOpen) {
      // Load Stripe with hard coded connected account
      const loadConnectedStripe = async () => {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_API_KEY, {
          stripeAccount: stripeAccountId
        });
        setStripePromise(stripe);
      };
      loadConnectedStripe();
    }
  }, [isOpen, stripeAccountId]);

  if (!stripePromise) {
    return null;
  }

  return (
    <Elements stripe={stripePromise}>
      <PerStayCardDrawerContent
        isOpen={isOpen}
        onClose={onClose}
        stripeAccountId={stripeAccountId}
        amount={amount}
        currency={currency}
        description={description}
        guest={guest}
        booking={booking}
        fullSecurityItem={fullSecurityItem}
        totalPrice={totalPrice}
        onPaymentSuccess={onPaymentSuccess}
      />
    </Elements>
  );
}; 